// Direct-OData stats client used by the Market Intelligence report page.
// Mirrors the CLI's PlunkMIClient (Plunk.Reporting/DataSources/PlunkMIClient.cs).
//
// Distinct from `market-intelligence.js` (the /v1/market-intelligence/*
// aggregator). These endpoints are the raw OData stats roll-ups the
// CLI uses to build its monthly markdown report.

import endpoints from './index';
import account from '../data/account';
import { headers, fetchWithRetry, parseBigSafe, qs } from './market-intelligence';
const v1Root = endpoints.product.root; // `${BASE}/v1` — same root for /campaign, /place, /location, /product

async function get(path, params) {
  const url = `${v1Root}${path}${qs(params)}`;
  const res = await fetchWithRetry(url, {
    method: 'GET',
    headers: await headers()
  });
  const text = await res.text();
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status} ${text}`);
  return parseBigSafe(text);
}

// --- Campaigns ---------------------------------------------------------------
// /v1/campaign/stats/dates/{start}/{end}?location=&account=
// Returns: { items: [ { campaign, location, type, date, impressions, videoViews, clicks, cost } ] }

export function getCampaignStats({
  location,
  start,
  end
}) {
  // `account` is the human-readable name ("RobbStucky"), not the
  // tenantId hash. The CLI mirrors this — see PlunkMIClient.cs
  // PullCampaignsAsync, which passes _cfg.Account.
  const acct = account.get();
  return get(`/campaign/stats/dates/${start}/${end}`, {
    location,
    account: acct.account
  });
}

// --- Places (per-market website rollup) --------------------------------------
// /v1/place/stats/dates/{start}/{end}?$groupBy=name
// Returns: { items: [ { name, views, sessions, uniqueUsers, conversions, avgTimeSpan } ] }

export function getPlaceStats({
  start,
  end
}) {
  return get(`/place/stats/dates/${start}/${end}`, {
    $groupBy: 'name'
  });
}

// --- Locations (zip rollups) -------------------------------------------------
// /v1/location/stats/dates/{start}/{end}?$groupBy=postal_code&place=X
// Or for city-resolved subset: ?$groupBy=postal_code&$where=city='X'

export function getZipsByPlace({
  place,
  start,
  end
}) {
  return get(`/location/stats/dates/${start}/${end}`, {
    $groupBy: 'postal_code',
    place
  });
}
export function getZipsByCity({
  city,
  start,
  end
}) {
  const where = `city = '${city.replace(/'/g, "''")}'`;
  return get(`/location/stats/dates/${start}/${end}`, {
    $groupBy: 'postal_code',
    $where: where
  });
}

// Fallback: explicit zip list (used for markets whose place isn't in
// location_geo_target — Wellen Park, Broward County).
export function getZipsByList({
  zips,
  start,
  end
}) {
  const quoted = zips.map(z => `'${z}'`).join(',');
  const where = `postal_code IN (${quoted})`;
  return get(`/location/stats/dates/${start}/${end}`, {
    $groupBy: 'postal_code',
    $where: where
  });
}

// --- Products by location (per-zip per-product) ------------------------------
// /v1/product/location/stats/dates/{start}/{end}?$groupBy=postal_code&$top=10000
// 10k cap is the Lambda payload limit workaround documented in the CLI.

export function getProductLocationStats({
  start,
  end
}) {
  return get(`/product/location/stats/dates/${start}/${end}`, {
    $groupBy: 'postal_code',
    $top: 10000
  });
}

// --- Cluster product performance --------------------------------------------
// /v1/product/performance/stats/{preset}?$top=15&$orderBy=views+desc
// Preset is one of: last_month, this_month, today, yesterday, last_7_days.

export function getClusterProductPerformance({
  preset,
  top = 15
}) {
  return get(`/product/performance/stats/${preset}`, {
    $top: top,
    $orderBy: 'views desc'
  });
}

// --- Date helpers ------------------------------------------------------------

// CLI range → preset mapping. Returns null if no preset matches (caller
// should treat as "no cluster perf data available for this window").
export function rangeToPreset(rangeKey) {
  // RANGE_OPTIONS values from range-options.js: 'today', 'yesterday',
  // 'last_7_days', 'this_month', 'last_month', etc.
  const presets = ['today', 'yesterday', 'last_7_days', 'this_month', 'last_month'];
  return presets.includes(rangeKey) ? rangeKey : null;
}

// Range token → { start, end } (yyyy-MM-dd) and the comparison window.
// Implemented in functions/range-window.js so the FULL global range
// vocabulary (this_week … last_year) and `custom_YYYYMMDD_YYYYMMDD` tokens
// resolve to real dates. Both return null for all_time / unknown tokens —
// callers must not fetch on null (the old fallback to "this month to date"
// silently served Sep 1–11 under a Jan–Jun header, 2026-09-11).
export { rangeToWindow, previousWindow } from '../functions/range-window';
import { rangeToWindow as _rangeToWindow, previousWindow as _previousWindow } from '../functions/range-window';
export default {
  getCampaignStats,
  getPlaceStats,
  getZipsByPlace,
  getZipsByCity,
  getZipsByList,
  getProductLocationStats,
  getClusterProductPerformance,
  rangeToPreset,
  rangeToWindow: _rangeToWindow,
  previousWindow: _previousWindow
};